import { useAuth, useOrganization } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import { slugify } from "@/lib/slugify";
import type { OrgProfile, OrgProfileUpsert } from "../components/org-settings/types";

export function useOrgProfile() {
  const { organization } = useOrganization();
  const { isLoaded, isSignedIn } = useAuth();
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const enabled = Boolean(organization) && isLoaded && isSignedIn;
  const queryKey = ["org-profile", organization?.id] as const;

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: async (): Promise<OrgProfile> => {
      if (!organization) throw new Error("No active organization");
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("org_profiles" as any)
        .select("*")
        .eq("clerk_org_id", organization.id)
        .maybeSingle();
      if (error) throw error;

      if (data) {
        const profile = data as unknown as OrgProfile;

        // Keep identity snapshots current. unit_name always tracks Clerk's
        // own org name live — public site rendering reads it without a
        // Clerk session, so it can't just call organization.name the way
        // this authenticated hook can. unit_slug only gets backfilled when
        // it's missing (e.g. the org's Clerk slug wasn't set yet at
        // profile-creation time, leaving the site unreachable at
        // /site/:slug with no way to fix it — see docs/site-builder-overhaul.md);
        // once set, a unit can deliberately diverge it via a future
        // settings UI, so an existing value must never be silently
        // overwritten by a later Clerk slug change.
        const patch: { unit_name?: string; unit_slug?: string } = {};
        if (profile.unit_name !== organization.name) patch.unit_name = organization.name;
        if (!profile.unit_slug) patch.unit_slug = organization.slug || slugify(organization.name, "unit");

        if (Object.keys(patch).length > 0) {
          const { data: resynced, error: resyncErr } = await supabase
            .from("org_profiles" as any)
            .update(patch as any)
            .eq("id", profile.id)
            .select()
            .single();
          if (!resyncErr && resynced) return resynced as unknown as OrgProfile;
        }
        return profile;
      }

      // Auto-create with defaults — unit_name/unit_slug snapshot Clerk's
      // own org name/slug at creation time (the default public routing
      // key), same "snapshot on write" pattern as the hub/calendar public
      // calendar feed's `public_calendar_slug`. Falls back to a slugified
      // org name when Clerk has no slug yet, so a site is never left
      // permanently unreachable at /site/:slug. unit_slug stays editable
      // afterward via a future settings UI; unit_name is kept in sync above.
      const { data: created, error: insertErr } = await supabase
        .from("org_profiles" as any)
        .insert({
          clerk_org_id: organization.id,
          unit_name: organization.name,
          unit_slug: organization.slug || slugify(organization.name, "unit"),
        } as any)
        .select()
        .single();
      if (insertErr) throw insertErr;
      return created as unknown as OrgProfile;
    },
  });

  const save = useMutation({
    mutationFn: async (updates: Partial<OrgProfileUpsert>): Promise<OrgProfile> => {
      const profile = query.data;
      if (!profile) throw new Error("No org profile loaded");
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("org_profiles" as any)
        .update(updates as any)
        .eq("id", profile.id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as OrgProfile;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    profile: query.data ?? null,
    loading: query.isLoading,
    saving: save.isPending,
    error: query.error instanceof Error ? query.error.message : save.error instanceof Error ? save.error.message : null,
    save: (updates: Partial<OrgProfileUpsert>) => save.mutateAsync(updates),
    reload: () => query.refetch(),
  };
}
